import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import type { ReactNode } from 'react';

import { company } from '~/company/Company';
import { useDateLang } from '~/hooks/useDateLang';
import { buildBadgeColorStyle } from '~/utils/color';

import styles from './BadgeV3.module.scss';
import type { BadgeV3Props } from './BadgeV3.types';

/**
 * BadgeV3 - Badge component for ProductCardV3
 *
 * Size specifications (sorted by size):
 * - XS mobile: height 16px, padding 2px 8px, font --text-50-medium
 * - XS web: height 24px, padding 4px 12px, font --text-200-regular
 * - S mobile: height 15px, padding 2px 8px, font --text-50-medium
 * - S web: height 20px, padding 2px 8px, font --text-100-regular
 * - M mobile: height 20px, padding 2px 8px, font --text-100-regular
 * - M web: height 24px, padding 4px 12px, font --text-200-medium
 * - L mobile: height 28px, padding 4px 12px, font --text-300-medium
 * - L web: height 36px, padding 9px 16px, font --text-300-medium
 *
 * Variant specifications:
 * - primary: Black background with white text (default for discounts)
 * - outline: Transparent background with gray stroke and dark text
 * - gray: Gray background with secondary text
 * - success: Success green background with success text
 * - multibuy: Green-dark background with white text (for active multibuy discount)
 *
 * Features:
 * - Supports delivery date formatting when deliveryDate prop is provided
 * - Can display text prefix before delivery date
 * - Auto-hides when no content provided
 * - Accepts catalog-supplied colors, overriding the variant when valid
 * - Keeps text on a single line, truncating with an ellipsis
 */
const BadgeV3 = ({
  size,
  variant,
  children,
  deliveryDate,
  dateFormat = 'dd.MM',
  text,
  className,
  badgeType,
  backgroundColor,
  textColor,
}: BadgeV3Props) => {
  const { locale } = useDateLang();

  if (!children && !deliveryDate && !text) {
    return null;
  }

  let content: ReactNode = children;
  if (deliveryDate || text) {
    content = (
      <>
        {text}
        {deliveryDate &&
          format(deliveryDate, dateFormat, {
            timeZone: company.config.timeZone,
            locale,
          })}
      </>
    );
  }

  const customColors = buildBadgeColorStyle(backgroundColor, textColor);

  return (
    <div
      className={classes([
        styles.badge,
        styles[`size_${size}`],
        styles[`variant_${variant}`],
        className,
      ])}
      data-company={company.name}
      data-badge-type={badgeType}
      style={customColors}
    >
      <span className={styles.content}>{content}</span>
    </div>
  );
};

export default BadgeV3;
